/**
 * Data Hub — console shell.
 *
 * Layout: one header card (title, actions, stats), then the artifact browser
 * in a second card. Uses ExecBridge common Button + TabsComponent.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";

import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  CalendarRange,
  Database,
  FileSearch,
  FlaskConical,
  GitBranch,
  Grid3x3,
  Inbox,
  LayoutGrid,
  LogOut,
  RotateCcw,
  Settings,
  ListChecks,
  ShieldAlert,
  Target,
  Upload,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Button from "../Components/Button";
import DropdownSwitcher from "../Components/DropdownSwitcher";
import CommonModal from "../Components/Modal";
import SyntheticDataModeBanner from "../Components/SyntheticDataModeBanner";
import TabsComponent from "../Components/Tab";
import { useOrgSyntheticDataToggle } from "../Hooks/useOrgSyntheticDataToggle";
import { ConfirmModalBody } from "./components/ConfirmModalBody";
import { CustomRubricPanel } from "./components/CustomRubricPanel";
import { SyntheticNotice } from "./components/DatasetPill";
import { ResetDialog } from "./components/ResetDialog";
import { RunConsole, type ActiveRun } from "./components/RunConsole";
import { RunWizard } from "./components/RunWizard";
import { LIVE_TABS } from "./data/adapter";
import { describeError } from "./data/api";
import {
  DATASET_KINDS,
  DATASET_LABELS,
  type DatasetKind,
} from "./data/dataset";
import { RO_SCORES_ENABLED } from "./data/flags";
import { hydrateActiveRun } from "./data/resume";
import { useActiveDataset } from "./hooks/useActiveDataset";
import { useAdapterData } from "./hooks/useAdapterData";
import { useDataHubAdapter } from "./hooks/useDataHubAdapter";
import { BenchmarkedTab } from "./tabs/BenchmarkedTab";
import { CategoryScoresTab } from "./tabs/CategoryScoresTab";
import { NormalizedTab } from "./tabs/NormalizedTab";
import { PipelineReportsTab } from "./tabs/PipelineReportsTab";
import { PipelineRunsTab } from "./tabs/PipelineRunsTab";
import { ProductionTab } from "./tabs/ProductionTab";
import { RoScoresTab } from "./tabs/RoScoresTab";
import { StagingTab } from "./tabs/StagingTab";
import { SummarizedTab } from "./tabs/SummarizedTab";
import MetricsWeightsSection from "../Container/MetricsDataManagementContainer/MetricsWeightsSection";
import RoConfigSection from "../Container/MetricsDataManagementContainer/RoConfigSection";
import { useAppDispatch } from "../Store/Hooks";

const TAB_IDS = [
  "runs",
  "qc",
  "staging",
  "production",
  "benchmarked",
  "normalized",
  "summarized",
  "category-scores",
  "ro-scores",
] as const;

type TabId = (typeof TAB_IDS)[number];
type GroupId = "pipeline" | "records" | "insights";

const TAB_LABELS: Record<TabId, string> = {
  runs: "Pipeline Runs",
  staging: "Staging Records",
  production: "Production Records",
  qc: "KPIs Reports",
  benchmarked: "Benchmarked",
  normalized: "Normalized",
  summarized: "Summarized",
  "category-scores": "Category Scores",
  "ro-scores": "R&O Scores",
};

const TAB_ICONS: Record<TabId, ReactNode> = {
  runs: <GitBranch className="h-4 w-4" />,
  staging: <Inbox className="h-4 w-4" />,
  production: <Database className="h-4 w-4" />,
  qc: <FileSearch className="h-4 w-4" />,
  benchmarked: <BarChart3 className="h-4 w-4" />,
  normalized: <Grid3x3 className="h-4 w-4" />,
  summarized: <CalendarRange className="h-4 w-4" />,
  "category-scores": <LayoutGrid className="h-4 w-4" />,
  "ro-scores": <Target className="h-4 w-4" />,
};

const TAB_GROUPS: {
  id: GroupId;
  label: string;
  icon: ReactNode;
  tabs: TabId[];
}[] = [
  {
    id: "pipeline",
    label: "Pipeline",
    icon: <GitBranch className="h-4 w-4" />,
    // KPIs Reports is the same PipelineReportsTab as the per-run Reports
    // button — keep only Pipeline Runs in the Pipeline group.
    tabs: ["runs"],
  },
  {
    id: "records",
    label: "Records",
    icon: <Database className="h-4 w-4" />,
    tabs: ["staging", "production"],
  },
  {
    id: "insights",
    label: "Insights",
    icon: <BarChart3 className="h-4 w-4" />,
    tabs: [
      "benchmarked",
      "normalized",
      "summarized",
      "category-scores",
      ...(RO_SCORES_ENABLED ? (["ro-scores"] as const) : []),
    ],
  },
];

function groupForTab(tab: TabId): GroupId {
  const group = TAB_GROUPS.find(g => g.tabs.includes(tab));
  return group?.id ?? "pipeline";
}

const ACTION_BTN =
  "h-9 shrink-0 whitespace-nowrap !rounded-lg !px-3.5 !py-0 text-sm";

function StatStrip({
  refreshKey,
  dataset,
}: {
  refreshKey: number;
  dataset: string;
}) {
  const adapter = useDataHubAdapter();
  const { data, loading } = useAdapterData(
    () => adapter.getStats(),
    // `dataset` is not read inside the fetch — the header carries it — but it
    // must stay in the dependency list, or the strip keeps showing the previous
    // dataset's counts after a switch.
    [refreshKey, dataset]
  );
  // Distinguish three states: loading, measured, and unmeasurable. A count we
  // could not read renders "—"; it must never be shown as 0 or as a sample.
  const cell = (v: number | null | undefined): number | string =>
    loading ? "…" : typeof v === "number" ? v : "—";
  const stats: { label: string; value: number | string }[] = [
    { label: "Staging", value: cell(data?.staging) },
    { label: "Production", value: cell(data?.timeseries) },
    { label: "Benchmarks", value: cell(data?.benchmarks) },
    { label: "Summaries", value: cell(data?.summaries) },
    { label: "Runs", value: cell(data?.runs) },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
      {stats.map(s => (
        <div
          key={s.label}
          className="rounded-xl border border-gray-100 bg-gray-50/80 px-3.5 py-2.5"
        >
          <p className="m-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            {s.label}
          </p>
          <p className="m-0 mt-1 text-xl font-semibold leading-none tabular-nums text-gray-900">
            {typeof s.value === "number" ? s.value.toLocaleString() : s.value}
          </p>
        </div>
      ))}
    </div>
  );
}

export type DataHubPageProps = {
  /** Display name for the active tenant/org, shown under the title. */
  orgName?: string;
  /** Console heading. Kept a prop so the host page and the nav label can agree. */
  title?: string;
  /**
   * When true (e.g. under Exec Ops → Metrics Upload), the host already provides
   * page chrome; title still renders so the console matches the standalone view.
   */
  embedded?: boolean;
  /**
   * When embedded, Import / KPI Config render into this node (parallel to
   * upload-type tabs). Dataset + Reset stay on the Metrics Upload title row.
   */
  headerActionsContainer?: HTMLElement | null;
  /**
   * Fired when an Import & Run workflow is active and unfinished, so the host
   * (e.g. upload-type pills) can ask before navigating away.
   */
  onRunActiveChange?: (active: boolean) => void;
  /**
   * Fired while the Import & Run pane is open (including after success), so the
   * host can fill the viewport and pin Continue at Sign Out level.
   */
  onWorkflowOpenChange?: (open: boolean) => void;
};

export function DataHubPage({
  orgName = "",
  title = "Data Hub",
  embedded = false,
  headerActionsContainer = null,
  onRunActiveChange,
  onWorkflowOpenChange,
}: DataHubPageProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [tab, setTab] = useState<TabId>("runs");
  const [lastTabByGroup, setLastTabByGroup] = useState<
    Partial<Record<GroupId, TabId>>
  >({ pipeline: "runs" });
  // Bumped when a run finishes so the stat strip and the active tab refetch.
  const [refreshKey, setRefreshKey] = useState(0);
  /** The in-flight run. Its steps render on the page, not in the dialog. */
  const [activeRun, setActiveRun] = useState<ActiveRun | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  // Vendor KPI Config (weights / coverage / cadence) — available from Metrics
  // Upload and standalone Temp Data Hub. Always On "Configure KPIs" deep-links
  // here with ?metricsTab=weights.
  const [kpiConfigOpen, setKpiConfigOpen] = useState(
    () => searchParams.get("metricsTab") === "weights"
  );
  // R&O Config (local tenant rubric — dimensions / metrics / weights / scores).
  const [roConfigOpen, setRoConfigOpen] = useState(false);
  // Custom rubric (this org's own metrics/categories: edit, delete, import).
  const [rubricOpen, setRubricOpen] = useState(false);
  const [dataset, selectDataset] = useActiveDataset();
  const {
    useSyntheticData,
    setUseSyntheticData,
    loading: syntheticToggleLoading,
    disabled: syntheticToggleDisabled,
  } = useOrgSyntheticDataToggle();
  const [resumeBusy, setResumeBusy] = useState<string | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  /** Pending dataset switch awaiting confirmation (null = no modal). */
  const [pendingDataset, setPendingDataset] = useState<DatasetKind | null>(
    null
  );
  const [datasetSwitchSaving, setDatasetSwitchSaving] = useState(false);
  /** True once Phase 2 finished — leaving no longer needs a confirm. */
  const [runFinished, setRunFinished] = useState(false);
  const [workflowHeaderActionsEl, setWorkflowHeaderActionsEl] =
    useState<HTMLElement | null>(null);
  const [workflowFooterActionsEl, setWorkflowFooterActionsEl] =
    useState<HTMLElement | null>(null);
  const [workflowStepperEl, setWorkflowStepperEl] =
    useState<HTMLElement | null>(null);

  // Keep Data Hub lane aligned with the org Synthetic toggle (Always On /
  // Hawkeye read the same flag). One control drives both.
  useEffect(() => {
    const next: DatasetKind = useSyntheticData ? "synthetic" : "production";
    if (dataset !== next) selectDataset(next);
  }, [useSyntheticData, dataset, selectDataset]);

  const currentDataset: DatasetKind = useSyntheticData
    ? "synthetic"
    : "production";

  const requestDatasetChange = (next: DatasetKind) => {
    if (next === currentDataset) return;
    setPendingDataset(next);
  };

  const cancelDatasetChange = () => {
    if (datasetSwitchSaving) return;
    setPendingDataset(null);
  };

  const confirmDatasetChange = async () => {
    if (!pendingDataset || datasetSwitchSaving) return;
    setDatasetSwitchSaving(true);
    try {
      const useSynthetic = pendingDataset === "synthetic";
      const ok = await setUseSyntheticData(useSynthetic);
      if (!ok) return;
      selectDataset(pendingDataset);
      setRefreshKey(k => k + 1);
      setPendingDataset(null);
    } finally {
      setDatasetSwitchSaving(false);
    }
  };

  const resumeRun = async (ledgerRunId: string) => {
    setResumeBusy(ledgerRunId);
    setResumeError(null);
    try {
      setActiveRun(await hydrateActiveRun(dispatch, ledgerRunId));
    } catch (err: any) {
      setResumeError(describeError(err));
    } finally {
      setResumeBusy(null);
    }
  };

  const activeGroup = groupForTab(tab);
  const activeGroupTabs =
    TAB_GROUPS.find(g => g.id === activeGroup)?.tabs ?? TAB_GROUPS[0].tabs;

  const groupTabs = useMemo(
    () =>
      TAB_GROUPS.map(g => ({
        id: g.id,
        icon: g.icon,
        label: g.label,
      })),
    []
  );

  const subTabs = useMemo(
    () =>
      activeGroupTabs.map(id => ({
        id,
        icon: TAB_ICONS[id],
        label: (
          <span
            className="inline-flex items-center"
            title={
              LIVE_TABS.has(id)
                ? "Live ExecBridge data"
                : "Sample data — this tab's backend lands in a later phase"
            }
          >
            {!LIVE_TABS.has(id) && (
              <span
                aria-hidden
                className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-400 align-middle"
              />
            )}
            {TAB_LABELS[id]}
          </span>
        ),
      })),
    [activeGroupTabs]
  );

  const selectTab = (id: TabId) => {
    setTab(id);
    setLastTabByGroup(prev => ({ ...prev, [groupForTab(id)]: id }));
  };

  const selectGroup = (id: GroupId) => {
    const group = TAB_GROUPS.find(g => g.id === id);
    if (!group) return;
    const next = lastTabByGroup[id] ?? group.tabs[0];
    selectTab(next);
  };

  const handleViewEnterpriseKpis = () => {
    const basePath = orgSlug ? `/${orgSlug}` : "";
    navigate(`${basePath}/always-on-channel?briefingTab=kpi`);
  };

  const showKpiConfig = kpiConfigOpen;
  const showRoConfig = roConfigOpen;
  const showRubric = rubricOpen;
  const showWorkflow = Boolean(activeRun);
  const showDedicatedPane =
    showKpiConfig || showRoConfig || showRubric || showWorkflow;
  const runNeedsLeaveConfirm = showWorkflow && !runFinished;

  useEffect(() => {
    if (!activeRun) setRunFinished(false);
  }, [activeRun]);

  useEffect(() => {
    onRunActiveChange?.(runNeedsLeaveConfirm);
    return () => onRunActiveChange?.(false);
  }, [runNeedsLeaveConfirm, onRunActiveChange]);

  useEffect(() => {
    onWorkflowOpenChange?.(showWorkflow);
    return () => onWorkflowOpenChange?.(false);
  }, [showWorkflow, onWorkflowOpenChange]);

  const exitDedicatedPane = () => {
    setLeaveConfirmOpen(false);
    if (showKpiConfig) {
      setKpiConfigOpen(false);
      if (searchParams.get("metricsTab") === "weights") {
        const next = new URLSearchParams(searchParams);
        next.delete("metricsTab");
        setSearchParams(next, { replace: true });
      }
    }
    if (showRoConfig) setRoConfigOpen(false);
    if (showRubric) setRubricOpen(false);
    if (showWorkflow) {
      setActiveRun(null);
      setRunFinished(false);
      setRefreshKey(k => k + 1);
    }
  };

  /** Guard every leave path except wizard Continue. */
  const requestLeaveDedicatedPane = () => {
    if (runNeedsLeaveConfirm) {
      setLeaveConfirmOpen(true);
      return;
    }
    exitDedicatedPane();
  };

  const shellCard = embedded
    ? "rounded-lg border border-gray-300 bg-white p-5"
    : "rounded-[15px] border border-gray-200 bg-white p-4 shadow-sm sm:p-5";
  const panelCard = embedded
    ? "overflow-hidden rounded-lg border border-gray-300 bg-white"
    : "overflow-hidden rounded-[15px] border border-gray-200 bg-white shadow-sm";

  /**
   * Top-right (parallel to upload-type tabs when embedded). Workflow exits via
   * Leave run / Abandon — no separate Back CTA in the tab row.
   */
  const tabRowActions = showWorkflow ? null : showKpiConfig ||
    showRoConfig ||
    showRubric ? (
    <Button
      type="button"
      variant="outline-primary"
      className={ACTION_BTN}
      onClick={requestLeaveDedicatedPane}
      icon={<ArrowLeft className="h-4 w-4" />}
    >
      {embedded ? "Back to Metrics Upload" : `Back to ${title}`}
    </Button>
  ) : (
    <>
      <Button
        type="button"
        variant="outline-primary"
        className={ACTION_BTN}
        onClick={() => setKpiConfigOpen(true)}
        icon={<Settings className="h-4 w-4" />}
      >
        KPI Config
      </Button>
      <Button
        type="button"
        variant="outline-primary"
        className={ACTION_BTN}
        onClick={() => setRoConfigOpen(true)}
        icon={<ShieldAlert className="h-4 w-4" />}
      >
        R&O Config
      </Button>
      <Button
        type="button"
        variant="outline-primary"
        className={ACTION_BTN}
        onClick={() => setRubricOpen(true)}
        icon={<ListChecks className="h-4 w-4" />}
      >
        Custom Rubric
      </Button>
      <Button
        type="button"
        variant="primary"
        className={ACTION_BTN}
        onClick={() => setWizardOpen(true)}
        icon={<Upload className="h-4 w-4" />}
      >
        Import & Run Workflow
      </Button>
    </>
  );

  /** Title-row CTAs: dataset dropdown + reset stay beside "Metrics Upload". */
  const datasetSwitcherOptions = useMemo(
    () =>
      DATASET_KINDS.map(kind => ({
        value: kind,
        label: DATASET_LABELS[kind],
        icon: kind === "synthetic" ? FlaskConical : Database,
      })),
    []
  );
  const activeDatasetValue = pendingDataset ?? currentDataset;
  const titleRowActions = !showDedicatedPane ? (
    <>
      <DropdownSwitcher
        className="shrink-0"
        value={activeDatasetValue}
        prefix="Dataset: "
        options={datasetSwitcherOptions}
        onChange={requestDatasetChange}
        disabled={
          syntheticToggleDisabled ||
          syntheticToggleLoading ||
          datasetSwitchSaving
        }
        ariaLabel={`Dataset: ${DATASET_LABELS[activeDatasetValue]}. Change dataset.`}
        listboxLabel="Dataset"
      />
      <Button
        type="button"
        variant="outline-primary"
        className={`${ACTION_BTN} !border-red-200 !text-red-600 hover:!bg-red-50 hover:!text-red-700`}
        onClick={() => setResetOpen(true)}
        icon={<RotateCcw className="h-4 w-4" />}
      >
        Reset Pipeline Data
      </Button>
    </>
  ) : null;

  const headerTitle = showKpiConfig
    ? "KPI Config"
    : showRoConfig
      ? "R&O Config"
      : showWorkflow
        ? "Import & Run Workflow"
        : title;
  const headerSubtitle = showKpiConfig
    ? "Configure category and metric weights, review data coverage, and set ingestion cadence."
    : showRoConfig
      ? "Review and edit Risk & Opportunity dimensions, metrics, and weights for this tenant."
      : showWorkflow
        ? "Column mapping, matching, import, QC and scoring run here. Use Leave run to return to Metrics Upload."
        : "End-to-end workflow for importing, cleansing and scoring customer KPI data.";

  const actionsPortalTarget =
    embedded && headerActionsContainer ? headerActionsContainer : null;
  const portalTabRowActions = Boolean(actionsPortalTarget);

  return (
    <section
      className={
        showWorkflow
          ? embedded
            ? "flex flex-col gap-3"
            : "flex min-h-[32rem] flex-col gap-3"
          : embedded
            ? "space-y-5"
            : "space-y-4"
      }
    >
      <CommonModal
        isOpen={leaveConfirmOpen}
        toggle={() => setLeaveConfirmOpen(false)}
        title="Leave this run?"
        size="sm"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline-primary"
              onClick={() => setLeaveConfirmOpen(false)}
            >
              Stay
            </Button>
            <Button onClick={exitDedicatedPane}>Leave run</Button>
          </div>
        }
      >
        <ConfirmModalBody
          icon={LogOut}
          tone="info"
          headline="Your progress is saved."
          detail="You can resume this run later from Pipeline Runs."
        />
      </CommonModal>

      <CommonModal
        isOpen={pendingDataset !== null}
        toggle={cancelDatasetChange}
        title="Switch dataset?"
        size="sm"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline-primary"
              onClick={cancelDatasetChange}
              disabled={datasetSwitchSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void confirmDatasetChange()}
              disabled={datasetSwitchSaving}
              loading={datasetSwitchSaving}
            >
              Switch to {pendingDataset ? DATASET_LABELS[pendingDataset] : ""}
            </Button>
          </div>
        }
      >
        <ConfirmModalBody
          icon={Database}
          tone={pendingDataset === "synthetic" ? "warning" : "info"}
          headline={
            pendingDataset === "synthetic"
              ? "Switch to Synthetic data?"
              : "Switch to Production data?"
          }
          detail={
            pendingDataset === "synthetic"
              ? "Pipeline views and KPIs will use sample (synthetic) data, kept separate from production."
              : "Pipeline views and KPIs will use live production data."
          }
        />
      </CommonModal>

      <CommonModal
        isOpen={Boolean(resumeError)}
        toggle={() => setResumeError(null)}
        title="Could not resume"
        size="sm"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setResumeError(null)}>OK</Button>
          </div>
        }
      >
        <ConfirmModalBody
          icon={AlertCircle}
          tone="danger"
          headline="Resume failed."
          detail={resumeError}
        />
      </CommonModal>

      {portalTabRowActions && actionsPortalTarget
        ? createPortal(tabRowActions, actionsPortalTarget)
        : null}

      {/* Header — title/actions/stats; embedded matches Ex Ops title structure */}
      <div className={`${shellCard} shrink-0`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
          <div className="min-w-0 flex-1">
            <p
              className={
                embedded
                  ? "m-0 text-lg font-semibold text-gray-900"
                  : "m-0 text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl"
              }
            >
              {headerTitle}
            </p>
            {!embedded &&
            !showDedicatedPane &&
            orgName &&
            orgName !== "Active organization" ? (
              <p className="m-0 mt-1 text-sm font-medium text-gray-900">
                {orgName}
              </p>
            ) : null}
            <p
              className={
                embedded
                  ? "m-0 mt-1 text-sm text-gray-600"
                  : "m-0 mt-1 max-w-2xl text-sm leading-relaxed text-gray-500"
              }
            >
              {headerSubtitle}
            </p>
          </div>

          <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-2 lg:ml-auto lg:w-auto">
            {showWorkflow ? (
              <div
                ref={setWorkflowHeaderActionsEl}
                className="flex flex-wrap items-center justify-end gap-2"
              />
            ) : null}
            {titleRowActions}
            {!portalTabRowActions && tabRowActions}
          </div>
        </div>

        {showWorkflow ? (
          <div ref={setWorkflowStepperEl} className="mt-4 shrink-0" />
        ) : null}

        {!showDedicatedPane && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <StatStrip refreshKey={refreshKey} dataset={dataset} />
          </div>
        )}
      </div>

      {showKpiConfig ? (
        <div
          className={`${panelCard} min-h-0 flex-1 overflow-auto px-5 pb-5 pt-2`}
        >
          <MetricsWeightsSection
            onViewEnterpriseKpis={handleViewEnterpriseKpis}
          />
        </div>
      ) : showRoConfig ? (
        <div
          className={`${panelCard} min-h-0 flex-1 overflow-auto px-5 pb-5 pt-2`}
        >
          <RoConfigSection />
        </div>
      ) : showRubric ? (
        <div
          className={`${panelCard} min-h-0 flex-1 overflow-auto px-5 pb-5 pt-4`}
        >
          <CustomRubricPanel />
        </div>
      ) : showWorkflow && activeRun ? (
        <>
          <div className={panelCard}>
            <RunConsole
              run={activeRun}
              onExit={exitDedicatedPane}
              onForceExit={exitDedicatedPane}
              onFinishedChange={setRunFinished}
              headerActionsContainer={workflowHeaderActionsEl}
              footerActionsContainer={workflowFooterActionsEl}
              footerPinned
              stepperContainer={workflowStepperEl}
              stepperPinned
              onPersist={resume =>
                setActiveRun(r => (r ? { ...r, resume } : r))
              }
              onRunComplete={() => {
                setRunFinished(true);
                setRefreshKey(k => k + 1);
              }}
            />
          </div>
          {/* Continue sits under the step content; page scrolls as one unit. */}
          <div
            ref={setWorkflowFooterActionsEl}
            className="sticky bottom-0 z-[1] shrink-0 border-t border-gray-200 bg-white px-4 py-3"
          />
        </>
      ) : (
        <>
          <ResetDialog
            open={resetOpen}
            onOpenChange={setResetOpen}
            orgName={orgName}
            onReset={() => setRefreshKey(k => k + 1)}
          />

          <RunWizard
            open={wizardOpen}
            onOpenChange={setWizardOpen}
            onStart={payload => {
              setActiveRun(payload);
            }}
          />

          {useSyntheticData ? (
            <SyntheticDataModeBanner className="mb-0" />
          ) : (
            <SyntheticNotice dataset={dataset} />
          )}

          {/* Artifact browser — group tabs left, sub-tabs right on one row */}
          <div className={panelCard}>
            <div className="flex flex-col gap-3 border-b border-gray-200 bg-white px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4">
              <TabsComponent
                tabs={groupTabs}
                activeTab={activeGroup}
                onTabChange={id => selectGroup(id as GroupId)}
                variant="secondary-segmented"
                segmentedLayout="fit"
                className="mb-0 shrink-0 whitespace-nowrap rounded-full"
              />
              <div className="min-w-0 overflow-x-auto sm:flex sm:justify-end">
                <TabsComponent
                  tabs={subTabs}
                  activeTab={tab}
                  onTabChange={id => selectTab(id as TabId)}
                  variant="tab-underline"
                  className="data-hub-tabs mb-0 w-max max-w-none flex-nowrap"
                />
              </div>
            </div>

            <div key={refreshKey} className="min-h-[280px]">
              {tab === "runs" && (
                <PipelineRunsTab onResume={resumeRun} resumeBusy={resumeBusy} />
              )}
              {tab === "staging" && <StagingTab />}
              {tab === "production" && <ProductionTab />}
              {tab === "qc" && <PipelineReportsTab />}
              {tab === "benchmarked" && <BenchmarkedTab />}
              {tab === "normalized" && <NormalizedTab />}
              {tab === "summarized" && <SummarizedTab />}
              {tab === "category-scores" && <CategoryScoresTab />}
              {tab === "ro-scores" && RO_SCORES_ENABLED && <RoScoresTab />}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default DataHubPage;
